import {
    alignFiltersWithSchemas,
    AuthContext,
    Badge,
    BaseButton,
    BaseNavLink,
    BatchCoderRuleRestoreDialog,
    BatchCoderRuleSuppressDialog,
    ClaimApi,
    ClaimCloseoutType,
    ClaimResponsibilityType,
    ClaimType,
    ProviderClaimResponsibilityCategoryType,
    ClaimTypeSelect,
    CoreFetch,
    DataTablePresenter,
    FilterMethod,
    formatDate,
    getOptions,
    getResponsibilityCategoryDescription,
    getResponsibilityDescription,
    IAuthContextProps,
    ICustomFilter,
    IFilterSchema,
    ISelectOption,
    ITableColumn,
    ITableFetchOptions,
    mapWithConcurrency,
    MULTI_STRING_MAX_VALUES,
    normalizeStorageFilters,
    ProviderClaim,
    ProviderClaimSortType,
    renderAmount,
    renderBoolean,
    renderDateTime,
    renderString,
    ResponsibilityBadge,
    ResponsibilitySummary,
    TableStorage,
    Tooltip,
    applyHiddenColumnsMigration,
    createdScope,
    IHiddenColumnsMigration,
    linkResponsibilityToTable,
    RESPONSIBILITY_COLUMNS_MIGRATION,
} from '@enter/core';
import {PageHeader, PageLayout, PageLayoutRow, ProviderMessagingContext} from 'components';
import _ from 'lodash';
import {inject, observer} from 'mobx-react';
import React, {useContext} from 'react';
import {useParams} from 'react-router-dom';
import {Row} from 'react-table';
import {DialogContext, IDialogContextProps} from 'services';
import {ClaimStore, IInjectedRootStore, IObservedRootStore, MetricsStore, parseSortBy} from 'stores';
import {ClaimService} from 'stores/claim.service';
import {coderRuleSuppressionApi} from 'stores/coder-rule-suppression.service';
import {BatchProviderCloseoutDialog} from './closeout-dialog/batch-closeout-dialog';
import {ClaimCloseoutBatchTarget} from './closeout-dialog/batch-closeout.util';
import {ClaimListBatchActionsToolbar} from './claim-list-batch-toolbar';
import * as S from './claim-listing.styles';

export const claimsColumnsBase: ITableColumn<ProviderClaim>[] = [
    {
        id: 'id',
        Header: 'Claim ID',
        accessor: 'id',
        disableSortBy: true,
        Cell: ({ cell }): React.ReactElement => <S.Cell>{cell.value}</S.Cell>,
    },
    {
        id: 'Responsibility',
        Header: 'Responsibility',
        accessor: 'claimResponsibilityType',
        sortType: ProviderClaimSortType.RESPONSIBILITY,
        minWidth: 130,
        Cell: ({ cell }): React.ReactElement => (
            <S.Cell>
                <Tooltip
                    direction="top"
                    variant="light"
                    title={cell.value ? <span>{getResponsibilityDescription(cell.value)}</span> : undefined}>
                    <ResponsibilityBadge size="large" responsibility={cell.value} />
                </Tooltip>
            </S.Cell>
        ),
    },

    {
        id: 'ResponsibilityCategory',
        Header: 'Responsibility Category',
        accessor: 'claimResponsibilityCategoryType',
        sortType: ProviderClaimSortType.RESPONSIBILITYCATEGORY,
        Cell: ({ cell }) => (
            <S.Cell>
                <Tooltip
                    direction="top"
                    variant="dark"
                    title={cell.value ? <span>{getResponsibilityCategoryDescription(cell.value)}</span> : undefined}>
                    {renderString(cell.value, { normalized: true, type: 'String' })}
                </Tooltip>
            </S.Cell>
        ),
        // Visible by default, like Responsibility (RESPONSIBILITY_COLUMNS_MIGRATION un-hides it for saved settings).
    },
    {
        id: 'ResponsibilityEvent',
        Header: 'Responsibility Event',
        accessor: 'claimResponsibilityEventDate',
        sortType: ProviderClaimSortType.RESPONSIBILITYEVENT,
        Cell: ({ cell }): React.ReactElement => <S.Cell>{renderDateTime(cell.value)}</S.Cell>,
        show: false,
    },
    {
        id: 'ResponsibilityChecked',
        Header: 'Responsibility Checked',
        accessor: 'claimResponsibilityCheckedDate',
        disableSortBy: true,
        Cell: ({ cell }): React.ReactElement => <S.Cell>{renderDateTime(cell.value)}</S.Cell>,
        show: false,
    },
    {
        id: 'Created',
        Header: 'Created',
        show: false,
        sortType: ProviderClaimSortType.CREATED,
        accessor: (claim: ProviderClaim): React.ReactElement | undefined => {
            if (claim && claim.created) {
                return (
                    <S.CellTooltip direction="top" title={<S.Title>{monthDayYear(claim.created)}</S.Title>}>
                        {monthDayYear(claim.created)}
                    </S.CellTooltip>
                );
            }
        },
    },
    {
        id: 'ServiceDates',
        Header: 'Service Dates',
        sortType: ProviderClaimSortType.FROMDOS,
        accessor: (claim: ProviderClaim): string | undefined => {
            const { fromDos, throughDos } = claim;

            const now: Date = new Date();
            const isThisYear = (dateStr: string): boolean => {
                const year = parseInt(dateStr.substring(0, 4), 10);
                return now.getFullYear() === year;
            };
            const fmt = (d: string): string => (isThisYear(d) ? monthDay(d) : monthDayYear(d));
            if (fromDos && throughDos && fromDos !== throughDos) {
                return `${fmt(fromDos)} – ${fmt(throughDos)}`;
            } else if (fromDos) {
                return `${monthDayYear(fromDos)}`;
            } else {
                return '–';
            }
        },
        Cell: ({ cell }): React.ReactElement => (
            <S.CellTooltip direction="top" title={<S.Title>{cell.value}</S.Title>}>
                {cell.value}
            </S.CellTooltip>
        ),
        defaultSort: { id: 'Created', desc: true },
    },
    {
        id: 'PatientExternalID',
        Header: 'Patient External ID',
        accessor: 'patientExternalId',
        disableSortBy: true,
        show: false,
    },
    {
        id: 'MemberID',
        Header: 'Member ID',
        accessor: 'primaryPayerSubscriberMemberId',
        disableSortBy: true,
        minWidth: 120,
        show: false,
        Cell: ({ cell }): React.ReactElement => (
            <S.CellTooltip direction="top" title={<S.Title>{cell.value || '–'}</S.Title>}>
                {cell.value || '–'}
            </S.CellTooltip>
        ),
    },
    {
        id: 'Patient',
        Header: 'Patient Name',
        sortType: ProviderClaimSortType.FIRSTNAME,
        minWidth: 130,
        accessor: (claim: ProviderClaim): string => {
            const { patientFirst, patientLast } = claim;
            if (patientFirst && patientLast) {
                return `${patientFirst} ${patientLast}`;
            } else {
                return '–';
            }
        },
        Cell: ({ cell }): React.ReactElement => (
            <S.CellTooltip direction="top" title={<S.Title>{cell.value}</S.Title>}>
                <S.PatientName>{cell.value}</S.PatientName>
            </S.CellTooltip>
        ),
    },
    {
        id: 'PatientNickname',
        Header: 'Preferred Name',
        disableSortBy: true,
        minWidth: 130,
        accessor: (claim: ProviderClaim): string => {
            const { patientNickname } = claim;
            if(patientNickname) {
                return `${patientNickname}`;
            } else {
                return '–';
            }
        },
        Cell: ({ cell }): React.ReactElement => (
            <S.CellTooltip direction="top" title={<S.Title>{cell.value}</S.Title>}>
                <S.PatientName>{cell.value}</S.PatientName>
            </S.CellTooltip>
        ),
    },
    {
        id: 'Payer',
        Header: 'Payer',
        accessor: 'primaryPayerName',
        disableSortBy: true,
        Cell: ({ cell }): React.ReactElement | undefined => (
            <S.CellTooltip direction="top" title={<S.Title>{cell.value}</S.Title>}>
                <S.PayerName>{cell.value}</S.PayerName>
            </S.CellTooltip>
        ),
    },
    {
        id: 'ClaimType',
        Header: 'Claim Type',
        accessor: 'type',
        Cell: ({ cell }): React.ReactElement => <S.Cell>{cell.value}</S.Cell>,
        disableSortBy: true,
        show: false,
    },
    {
        id: 'TotalCharges',
        Header: () => <S.HeaderCurrency>Total Charges</S.HeaderCurrency>,
        description: 'The totaled charge amounts from all service lines on the claim',
        accessor: 'chargeAmount',
        sortType: ProviderClaimSortType.CHARGE,
        minWidth: 80,
        Cell: ({ cell }): React.ReactElement => <S.CellCurrency>{renderAmount(cell.value)}</S.CellCurrency>,
    },
    {
        id: 'PayerAllowed',
        Header: () => <S.HeaderCurrency>Payer Allowed</S.HeaderCurrency>,
        description: 'The total amount the payer has allowed from the charges',
        accessor: 'allowedAmount',
        disableSortBy: true,
        minWidth: 80,
        Cell: ({ cell }): React.ReactElement => <S.CellCurrency>{renderAmount(cell.value)}</S.CellCurrency>,
    },
    {
        id: 'PayerPaid',
        Header: () => <S.HeaderCurrency>Payer Paid</S.HeaderCurrency>,
        description: 'The total amount the payer has paid from the charges',
        accessor: 'payerAmount',
        sortType: ProviderClaimSortType.PAYERPAID,
        minWidth: 80,
        Cell: ({ cell }): React.ReactElement => <S.CellCurrency>{renderAmount(cell.value)}</S.CellCurrency>,
    },
    {
        id: 'PatientAmount',
        Header: () => <S.HeaderCurrency>Patient Amount</S.HeaderCurrency>,
        description: 'The patient responsibility amount',
        accessor: 'patientAmount',
        disableSortBy: true,
        minWidth: 80,
        Cell: ({ cell }): React.ReactElement => <S.CellCurrency>{renderAmount(cell.value)}</S.CellCurrency>,
    },
    {
        id: 'ChargemasterAllowedAmount',
        Header: () => <S.HeaderCurrency>Chargemaster Allowed</S.HeaderCurrency>,
        description:
            'Also known as the acceptable amount, it is the total minimum allowed amount expected from this claim',
        accessor: 'chargemasterAllowedAmount',
        disableSortBy: true,
        minWidth: 80,
        show: false,
        Cell: ({ cell }): React.ReactElement => <S.CellCurrency>{renderAmount(cell.value)}</S.CellCurrency>,
    },
    {
        id: 'PatientInvoiceAmount',
        Header: () => <S.HeaderCurrency>Patient Invoice Amount</S.HeaderCurrency>,
        description:
            "The amount the patient's guarantor has been invoiced. This can differ from the patient amount when provider specific discounts are applied",
        accessor: 'patientInvoiceAmount',
        disableSortBy: true,
        minWidth: 80,
        show: false,
        Cell: ({ cell }): React.ReactElement => <S.CellCurrency>{renderAmount(cell.value)}</S.CellCurrency>,
    },
    {
        id: 'PatientInvoicePaidAmount',
        Header: () => <S.HeaderCurrency>Patient Invoice Paid</S.HeaderCurrency>,
        description: "The amount the patient's guarantor has paid to date",
        accessor: 'patientInvoicePaidAmount',
        disableSortBy: true,
        minWidth: 80,
        show: false,
        Cell: ({ cell }): React.ReactElement => <S.CellCurrency>{renderAmount(cell.value)}</S.CellCurrency>,
    },
    // Commented out: 'invoiceNumber' property no longer exists in API model (ProviderClaim)
    // {
    //     id: 'InvoiceNumber',
    //     Header: 'Invoice',
    //     accessor: 'invoiceNumber',
    //     disableSortBy: true,
    //     show: false,
    // },
    {
        id: 'Closeout',
        Header: 'Closeout',
        accessor: 'closeoutType',
        disableSortBy: true,
        show: false,
        Cell: ({ cell, row }) => (
            <Badge>{row.original.closeoutVoid ? 'VOIDED' : cell.value}</Badge>
        ),
    },
];

// Column options the support claims list offers that `ProviderClaim` can also serve (FE-124).
// Hidden by default, and kept out of `claimsColumnsBase` so the guarantor claims table does not
// grow them too. Support's Patient Invoice ID, Collection Status Details, Collection Strategy,
// Tags, Latest Submitted, Latest Payment and Active are absent from the provider model.
export const claimsColumnsFromSupport: ITableColumn<ProviderClaim>[] = [
    {
        id: 'GuarantorID',
        Header: 'Guarantor ID',
        accessor: 'guarantorId',
        disableSortBy: true,
        show: false,
        Cell: ({ cell }): React.ReactElement => <S.Cell>{cell.value || '–'}</S.Cell>,
    },
    {
        id: 'Payments',
        Header: 'Payments Count',
        accessor: 'payments',
        disableSortBy: true,
        show: false,
        // Zero is a count, not a missing value.
        Cell: ({ cell }): React.ReactElement => <S.Cell>{_.isNil(cell.value) ? '–' : cell.value}</S.Cell>,
    },
    {
        id: 'CollectionStatus',
        Header: 'Collection Status',
        accessor: 'collectionStatus',
        disableSortBy: true,
        show: false,
        Cell: ({ cell }): React.ReactElement => (
            <S.Cell>{renderString(cell.value, { normalized: true, type: 'String' })}</S.Cell>
        ),
    },
    {
        id: 'AccountNumber',
        Header: 'Account Number',
        accessor: 'accountNumber',
        disableSortBy: true,
        show: false,
        Cell: ({ cell }): React.ReactElement => <S.Cell>{cell.value || '–'}</S.Cell>,
    },
    {
        id: 'ClaimBundleId',
        Header: 'Bundle ID',
        accessor: 'claimBundleId',
        disableSortBy: true,
        show: false,
        Cell: ({ cell }): React.ReactElement => <S.Cell>{cell.value || '–'}</S.Cell>,
    },
    {
        id: 'CloseoutDesc',
        Header: 'Closeout Description',
        accessor: 'closeoutDescription',
        disableSortBy: true,
        show: false,
        Cell: ({ cell }): React.ReactElement => (
            <S.CellTooltip direction="top" title={<S.Title>{cell.value || '–'}</S.Title>}>
                {cell.value || '–'}
            </S.CellTooltip>
        ),
    },
    {
        id: 'MaximumGuarantorResponsibility',
        Header: () => <S.HeaderCurrency>Maximum Guarantor Responsibility</S.HeaderCurrency>,
        accessor: 'maximumGuarantorResponsibility',
        disableSortBy: true,
        minWidth: 80,
        show: false,
        Cell: ({ cell }): React.ReactElement => <S.CellCurrency>{renderAmount(cell.value)}</S.CellCurrency>,
    },
    {
        id: 'Paperwork',
        Header: 'Paperwork',
        accessor: 'paperwork',
        disableSortBy: true,
        show: false,
        // An absent flag is unknown, not "no paperwork" (NS-3).
        Cell: ({ cell }): React.ReactElement => (
            <S.Cell>{_.isNil(cell.value) ? '–' : renderBoolean(cell.value)}</S.Cell>
        ),
    },
    {
        id: 'BillingEnabled',
        Header: 'Billing Enabled',
        description: 'The guarantor has a payment link for this claim',
        accessor: (claim: ProviderClaim): boolean => !!claim.patientInvoicePaymentUrl,
        disableSortBy: true,
        show: false,
        // Support draws a cross when there is no payment link. A missing link is not proof that
        // billing is off — the invoice may not exist yet — so only the positive is asserted (NS-3).
        Cell: ({ cell }): React.ReactElement => <S.Cell>{cell.value ? renderBoolean(true) : '–'}</S.Cell>,
    },
];

// Saved column settings would otherwise show every column above — see hidden-columns-migration.
const SUPPORT_COLUMNS_MIGRATION: IHiddenColumnsMigration = {
    id: 'fe-124-support-columns',
    columnIds: _.compact(_.map(claimsColumnsFromSupport, 'id')),
};

export const claimsColumns: ITableColumn<ProviderClaim>[] = [
    ...claimsColumnsBase,
    ...claimsColumnsFromSupport,
    {
        id: 'Action',
        Header: '',
        disableSortBy: true,
        Cell: ({ row }): React.ReactElement => {
            const messagingContext = useContext(ProviderMessagingContext);

            const handleCreateMessage = () => {
                messagingContext.openPanel?.({
                    claimId: row.original.id,
                    isComposeOpen: true,
                });
            };
            return (
                <S.ActionButton
                    claimId={row.original.id as string}
                    actions={[
                        {
                            label: 'Create new message',
                            onClick: handleCreateMessage,
                        },
                    ]}
                />
            );
        },
    },
];

export const claimsFilterSchemas: IFilterSchema[] = [
    {
        // Bulk claim-ID filter: paste a list, get those claims. The API reads the comma-joined
        // value as OR, so this stays a normal server-side filter — it pages, and it composes with
        // Payer / dates / the rest instead of replacing the table.
        label: 'ID',
        filterId: 'filterId',
        filterType: 'MultiString',
        placeholder: `Paste up to ${MULTI_STRING_MAX_VALUES} Claim IDs, one per line`,
        columnId: 'Id',
        rows: 5,
    },
    {
        label: 'Account Number',
        filterId: 'filterAccountNumber',
        filterType: 'String',
        columnId: 'AccountNumber',
    },
    {
        label: 'From Date of Service',
        filterId: 'filterFromDos',
        filterType: 'Date',
        columnId: 'ServiceDates',
    },
    {
        label: 'Through Date of Service',
        filterId: 'filterThroughDos',
        filterType: 'Date',
        columnId: 'ThroughDos',
    },
    {
        label: 'Patient First Last',
        filterId: 'filterFirstLast',
        filterType: 'String',
        columnId: 'Patient',
    },
    {
        label: 'Patient External ID',
        filterId: 'filterExternalId',
        filterType: 'String',
        columnId: 'ExternalID',
    },
    {
        label: 'Member ID',
        placeholder: 'Search by Member ID',
        filterId: 'filterPayerSubscriberId',
        columnId: 'payerSubscriberId',
        filterType: 'AsyncSelect',
        loadOptions: async (query): Promise<ISelectOption[]> => {
            const payerSubscribers = await ClaimService.queryPayerSubscribersByMemberId(query);
            return _.map(payerSubscribers || [], ps => {
                const label = ps.payer?.name 
                    ? `${ps.payer.name} - ${ps.memberId}`
                    : ps.subscriber 
                        ? `${ps.subscriber.first} ${ps.subscriber.last} - ${ps.memberId}`
                        : ps.memberId || ps.id || '';
                return {
                    label,
                    value: ps.id || '',
                };
            });
        },
    },
    {
        label: 'Payer',
        filterId: 'filterPayerId',
        columnId: 'Payer',
        filterType: 'AsyncSelect',
        loadOptions: async (query): Promise<ISelectOption[]> => {
            const payers = await ClaimService.queryPayers(query);
            return _.map(payers, p => ({
                label: p.name,
                value: p.id,
            }));
        },
    },
    {
        label: 'Payment Control Number',
        filterId: 'filterPaymentControlNumber',
        filterType: 'String',
        columnId: 'PaymentControlNumber',
    },
    {
        label: 'Payment Count',
        filterId: 'filterPaymentCount',
        filterType: 'Number',
        columnId: 'PaymentCount',
    },
    {
        label: 'Payment Count Settled',
        filterId: 'filterPaymentCountSettled',
        filterType: 'Number',
        columnId: 'PaymentCountSettled',
    },
    {
        label: 'Payment Count Unsettled',
        filterId: 'filterPaymentCountUnsettled',
        filterType: 'Number',
        columnId: 'PaymentCountUnsettled',
    },
    {
        label: 'Payment Date',
        filterId: 'filterPaymentDate',
        filterType: 'Date',
        columnId: 'PaymentDate',
    },
    {
        label: 'Provider',
        filterId: 'filterProviderId',
        columnId: 'Provider',
        filterType: 'AsyncSelect',
        loadOptions: async (query): Promise<ISelectOption[]> => {
            const providers = await ClaimService.queryProviders(query);
            return _.map(providers, p => ({
                label: `${p.first} ${p.last}`,
                value: p.id,
            }));
        },
    },
    {
        label: 'Guarantor',
        filterId: 'filterGuarantorId',
        columnId: 'Guarantor',
        filterType: 'AsyncSelect',
        loadOptions: async (query): Promise<ISelectOption[]> => {
            const guarantors = await ClaimService.queryGuarantors(query);
            return _.map(guarantors, p => ({
                label: `${p.first} ${p.last}`,
                value: p.id,
            }));
        },
    },
    {
        label: 'Patient',
        filterId: 'filterPatientId',
        columnId: 'Patient',
        filterType: 'AsyncSelect',
        loadOptions: async (query): Promise<ISelectOption[]> => {
            const patients = await ClaimService.queryPatients(query);
            return _.map(patients, p => ({
                label: `${p.first} ${p.last}`,
                value: p.id,
            }));
        },
    },
    {
        label: 'Seller',
        filterId: 'filterSellerId',
        columnId: 'Seller',
        filterType: 'AsyncSelect',
        loadOptions: async (query): Promise<ISelectOption[]> => {
            const sellers = await ClaimService.querySellers(query);
            return _.map(sellers, p => ({
                label: `${p.first} ${p.last}`,
                value: p.id,
            }));
        },
    },
    {
        label: 'Facility',
        filterId: 'filterFacilityId',
        columnId: 'Facility',
        filterType: 'AsyncSelect',
        loadOptions: async (query): Promise<ISelectOption[]> => {
            const facilities = await ClaimService.queryFacilities(query);
            return _.map(facilities, f => ({
                label: `${f.name}`,
                value: f.id,
            }));
        },
    },
    {
        label: 'Subscriber',
        filterId: 'filterSubscriberId',
        columnId: 'Subscriber',
        filterType: 'AsyncSelect',
        loadOptions: async (query): Promise<ISelectOption[]> => {
            const subscribers = await ClaimService.querySubscribers(query);
            return _.map(subscribers, s => ({
                label: `${s.first} ${s.last}`,
                value: s.id,
            }));
        },
    },
    {
        label: 'Payment',
        filterId: 'filterPaymentId',
        columnId: 'PaymentId',
        filterType: 'AsyncSelect',
        loadOptions: async (query): Promise<ISelectOption[]> => {
            const payments = await ClaimService.queryPayments(query);
            return _.map(payments, payment => {
                const payerAmount =
                    payment.payerAmount &&
                    `Payer amount: ${payment.payerAmount.currency} ${payment.payerAmount.amount}`;

                return {
                    label: _.compact([payerAmount, payment.payerControlNumber]).join(', '),
                    value: payment.id || '',
                };
            });
        },
    },
    {
        label: 'Created',
        filterId: 'filterCreated',
        filterType: 'DateTime',
        columnId: 'Created',
    },
    {
        label: 'Prior Authorization Number',
        filterId: 'filterPriorAuthorizationNumber',
        filterType: 'String',
        columnId: 'PriorAuthorizationNumber',
    },
    {
        label: 'Procedure Code',
        filterId: 'filterProcedure',
        filterType: 'String',
        columnId: 'Procedure',
    },
    {
        label: 'Modifier',
        filterId: 'filterModifier',
        filterType: 'String',
        columnId: 'Modifier',
    },
    {
        label: 'Service Charge',
        filterId: 'filterServiceCharge',
        filterType: 'String',
        columnId: 'ServiceCharge',
    },
    {
        label: 'Claim bundle ID',
        filterId: 'filterClaimBundleId',
        filterType: 'String',
        columnId: 'ClaimBundleId',
    },
    {
        label: 'Invoice Number',
        filterId: 'filterInvoiceNumber',
        filterType: 'String',
        columnId: 'InvoiceNumber',
    },
    {
        label: 'Responsibility',
        filterId: 'filterClaimResponsibilityType',
        filterType: 'Select',
        options: getOptions(ClaimResponsibilityType),
        columnId: 'Status',
    },
    {
        label: 'Responsibility Category',
        filterId: 'filterClaimResponsibilityCategoryType',
        filterType: 'Select',
        columnId: 'ResponsibilityCategory',
        options: getOptions(ProviderClaimResponsibilityCategoryType, { normalize: true }),
    },
    {
        label: 'Responsibility Event',
        filterId: 'filterClaimResponsibilityEvent',
        filterType: 'Date',
        columnId: 'ResponsibilityEvent',
    },
    {
        label: 'Total Charges',
        filterId: 'filterCharge',
        filterType: 'Number',
        columnId: 'TotalCharges',
    },
    {
        label: 'Payer Paid',
        filterId: 'filterPayerAmount',
        filterType: 'Number',
        columnId: 'PayerPaid',
    },
    {
        label: 'Closeout',
        filterId: 'filterClaimCloseoutType',
        filterType: 'Select',
        columnId: 'Closeout',
        options: getOptions(ClaimCloseoutType),
    },
];

// The table's search box — the same patient-name search as the support claims list.
export function claimsFilterParser(value: string): ICustomFilter[][] {
    return [
        [
            {
                filterType: 'String',
                filterId: 'filterFirstLast',
                value: value || undefined,
                filterMethod: FilterMethod.LK,
            },
        ],
    ];
}

interface IClaimListingProps extends IObservedRootStore {
    authContext: IAuthContextProps;
    dialogContext: IDialogContextProps;
}

interface IClaimListingState {
    isLoading: boolean;
    claimType: ClaimType,
    fetchOptions: ITableFetchOptions | undefined;
    // dateRangeFilter: ICustomFilter | undefined;
}

// Date formatting utilities - using UTC to match backend
const monthDay = (date: Date | string | null | undefined) => formatDate(date, 'MMM d');
const monthDayYear = (date: Date | string | null | undefined) => formatDate(date, 'MMM d, yyyy');

@inject('rootStore')
@observer
class ClaimListPage extends React.Component<IClaimListingProps, IClaimListingState> {
    private presenter: DataTablePresenter<ProviderClaim>;
    private cachedFetchOptions: ITableFetchOptions|undefined;
    private claimType: ClaimType;
    /** Stops keeping the responsibility widget and the table filters in step (FE-151). */
    private unlinkResponsibility: (() => void) | undefined;

    constructor(props: IClaimListingProps) {
        super(props);

        const claimTypes: ClaimType[] = props.rootStore?.metricsStore.selectedClaimTypes ? props.rootStore?.metricsStore.selectedClaimTypes : [ClaimType.PROFESSIONAL];
        const claimType: ClaimType = (claimTypes.indexOf(ClaimType.INSTITUTIONAL) > -1) ? ClaimType.INSTITUTIONAL : ClaimType.PROFESSIONAL;

        this.state = {
            isLoading: false,
            fetchOptions: undefined,
            claimType: claimType,
            // dateRangeFilter: undefined,
        };

        // Aligned with the current schema: an ID filter saved before ID became a multi-value field
        // would otherwise come back with a method the panel cannot show (see alignFiltersWithSchemas).
        const filters: ICustomFilter[] = alignFiltersWithSchemas(
            normalizeStorageFilters(TableStorage.getItem('claim-listing-table', 'filters') || []),
            claimsFilterSchemas,
        );

        // Before the presenter reads the saved hidden columns in its constructor.
        applyHiddenColumnsMigration('claim-listing-table', SUPPORT_COLUMNS_MIGRATION);
        applyHiddenColumnsMigration('claim-listing-table', RESPONSIBILITY_COLUMNS_MIGRATION);

        this.presenter = new DataTablePresenter<ProviderClaim>({
            name: 'claim-listing-table',
            data: [],
            columns: claimsColumns,
            filterSchemas: claimsFilterSchemas,
            filters,
            filterParser: claimsFilterParser,
        });
        this.cachedFetchOptions = undefined;
        this.claimType = claimType;
    }

    public componentDidMount(): void {
        document.title = 'ENTER Platform - Claims';
        this.unlinkResponsibility = linkResponsibilityToTable(
            this.props.rootStore.claimResponsibilityStore,
            this.presenter,
            claimsFilterSchemas,
        );
    }
    public componentWillUnmount(): void {
        this.authContext.cancelGroup('claim-listing');
        this.unlinkResponsibility?.();
        this.unlinkResponsibility = undefined;
    }
    // public async componentDidUpdate(): Promise<void> {
    // const { fetchOptions } = this.state;
    // const newDateRangeFilter = this.createDateRangeFilter();
    // const isSameFilter = JSON.stringify(newDateRangeFilter) === JSON.stringify(dateRangeFilter);

    // trigger re-fetch if date filter changed
    // if (fetchOptions) {
    //     // this.setState({ dateRangeFilter: newDateRangeFilter });

    //     // manually toggle table columns to match the new date range filter
    // const toggledColumns = this.updateColumnsForDateRange(this.presenter.columns);
    //     const ids = ['ServiceDates', 'Created'];
    //     _.forEach(ids, id => {
    //         const isHidden = _.indexOf(this.presenter.hiddenColumnIds, id) > -1;
    //         const column = _.find(toggledColumns, { id });
    //         if (column && column.show && isHidden) {
    //             this.presenter.onColumnToggle(id);
    //         }
    //     });

    // update sort by columns
    // const sortBy = [];
    // if (_.find(this.presenter.columns, { id: 'ServiceDates', show: true })) {
    //     sortBy.push({ id: 'ServiceDates', desc: true });
    // }
    // if (_.find(this.presenter.columns, { id: 'Created', show: true })) {
    //     sortBy.push({ id: 'Created', desc: true });
    // }

    // // NB: need to reload presenter to change defaults
    // const tableDefaults = this.presenter.getTableDefaults();
    // this.presenter.saveTableDefaults({ ...tableDefaults, pageIndex: 0, pageSize: 0, sortBy });
    // this.presenter = new DataTablePresenter({
    //     ...this.presenter,
    // });
    // }
    // }

    public onClaimTypeChange(type: ClaimType, metricsStore:MetricsStore|undefined): void {
        if(metricsStore && this.cachedFetchOptions) {
            metricsStore.selectClaimType(type);
            this.claimType = type;
            this.onFetch(this.cachedFetchOptions);
        }
    }

    public render(): React.ReactElement {
        const { metricsStore } = this.props.rootStore;
        const selectedClaimIds = this.getSelectedClaimIds();
        return (
            <PageLayout>
                <PageHeader title="Claims" hideDateRange={false}>
                    <S.HeaderActions>
                        <ClaimTypeSelect value={metricsStore.selectedClaimTypes} onChange={(type:ClaimType) => {this.onClaimTypeChange(type, metricsStore)}} />
                        <CreateClaimButton />
                    </S.HeaderActions>
                </PageHeader>

                <PageLayoutRow>
                    <ResponsibilitySummary store={this.props.rootStore.claimResponsibilityStore} />
                </PageLayoutRow>

                <PageLayoutRow expand={true}>
                    <S.DataTableCard size="large">
                        <S.ScopeNotice store={this.props.rootStore.claimResponsibilityStore} filters={this.presenter.validFilters} />
                        <S.ClaimTable
                            presenter={this.presenter}
                            isLoading={this.state.isLoading}
                            onFetch={this.onFetch}
                            getRowHref={this.getRowHref}
                            showExport={true}
                            selectableRows={true}
                            renderToolbar={() => (
                                <ClaimListBatchActionsToolbar
                                    selectedCount={selectedClaimIds.length}
                                    onCloseout={this.handleCloseoutBatch}
                                    onSuppressCoderRules={this.handleSuppressCoderRules}
                                    onRestoreCoderRules={this.handleRestoreCoderRules}
                                />
                            )}
                        />
                    </S.DataTableCard>
                    {/* Add dependency so date changes cause re-render*/}
                    {/* <S.Hidden>
                        {this.dateRangeType}
                        {JSON.stringify(this.dateRange)}
                    </S.Hidden> */}
                </PageLayoutRow>
            </PageLayout>
        );
    }

    private get authContext(): IAuthContextProps {
        return this.props.authContext;
    }

    private get dialogContext(): IDialogContextProps {
        return this.props.dialogContext;
    }

    private get injected(): IInjectedRootStore {
        return this.props as IInjectedRootStore;
    }

    private get claimStore(): ClaimStore {
        return this.injected.rootStore.claimStore;
    }

    // private get dateRangeStore(): DateRangeStore {
    //     return this.injected.rootStore.dateRangeStore;
    // }

    // private get dateRangeType(): DateRangeType {
    //     return this.dateRangeStore.dateRangeType;
    // }

    // private get dateRange(): [Date, Date] {
    //     return this.dateRangeStore.dateRange;
    // }

    // private get startDate(): Date {
    //     return this.dateRangeStore.dateRange[0];
    // }

    // private get endDate(): Date {
    //     return this.dateRangeStore.dateRange[1];
    // }

    private getSelectedClaimIds = (): string[] => {
        return Object.keys(this.presenter.selectedIds).filter(id => this.presenter.selectedIds[id]);
    };

    private handleSuppressCoderRules = (): void => {
        this.showCoderRuleBatchDialog('suppress');
    };

    private handleRestoreCoderRules = (): void => {
        this.showCoderRuleBatchDialog('restore');
    };

    private showCoderRuleBatchDialog = (action: 'suppress' | 'restore'): void => {
        const claimIds = this.getSelectedClaimIds();
        if (claimIds.length === 0) {
            return;
        }

        const handleClose = (): void => {
            this.dialogContext.hideDialog();
        };

        // A completed batch changes what the next batch would do, so the selection is dropped and the
        // listing re-read rather than left showing the state the rules were in before the run.
        const handleComplete = (): void => {
            this.presenter.onSelectedUpdate({});
            if (this.cachedFetchOptions) {
                void this.onFetch(this.cachedFetchOptions);
            }
        };

        const Dialog = action === 'suppress' ? BatchCoderRuleSuppressDialog : BatchCoderRuleRestoreDialog;

        this.dialogContext.showDialog({
            props: {
                onClose: handleClose,
                disableBackdropClick: true,
                disableEscapeKeyDown: true,
                renderTitle: action === 'suppress' ? 'Suppress coder rules' : 'Restore coder rules',
                fullWidth: true,
                maxWidth: 'sm',
            },
            Component: (
                <Dialog
                    claimIds={claimIds}
                    api={coderRuleSuppressionApi}
                    onCancel={handleClose}
                    onComplete={handleComplete}
                />
            ),
        });
    };

    // The table only holds the currently loaded page, so a claim selected on another page has no
    // row here to read `closeoutVoid` from — it has to be fetched to know whether the batch is
    // allowed to reopen it.
    private getSelectedClaimTargets = async (): Promise<ClaimCloseoutBatchTarget[]> => {
        const claimIds = this.getSelectedClaimIds();
        const loadedById = new Map(this.presenter.data.map(claim => [claim.id as string, claim]));
        const missingIds: string[] = [];

        const targets: ClaimCloseoutBatchTarget[] = claimIds.map(id => {
            const claim = loadedById.get(id);
            if (claim) {
                return { id, closeoutVoid: !!claim.closeoutVoid };
            }
            missingIds.push(id);
            return { id, closeoutVoid: false };
        });

        if (missingIds.length === 0) {
            return targets;
        }

        const fetchedClaims = await mapWithConcurrency(missingIds, 10, id =>
            CoreFetch<ClaimApi>(ClaimApi).getProviderClaim({ id }),
        );
        const fetchedById = new Map(fetchedClaims.map(claim => [claim.id as string, claim]));

        return targets.map(target => {
            const fetchedClaim = fetchedById.get(target.id);
            return fetchedClaim ? { id: target.id, closeoutVoid: !!fetchedClaim.closeoutVoid } : target;
        });
    };

    private handleCloseoutBatch = async (): Promise<void> => {
        const claimIds = this.getSelectedClaimIds();
        if (claimIds.length === 0) {
            return;
        }

        const handleClose = (): void => {
            this.dialogContext.hideDialog();
        };

        const handleComplete = (): void => {
            this.presenter.onSelectedUpdate({});
            if (this.cachedFetchOptions) {
                void this.onFetch(this.cachedFetchOptions);
            }
        };

        let targets: ClaimCloseoutBatchTarget[];
        try {
            targets = await this.getSelectedClaimTargets();
        } catch (err) {
            this.dialogContext.showError(err);
            return;
        }

        this.dialogContext.showDialog({
            props: {
                onClose: handleClose,
                disableBackdropClick: true,
                disableEscapeKeyDown: true,
                renderTitle: 'Close out claims',
                fullWidth: true,
                maxWidth: 'sm',
            },
            Component: <BatchProviderCloseoutDialog targets={targets} onCancel={handleClose} onComplete={handleComplete} />,
        });
    };

    private onFetch = async (fetchOptions: ITableFetchOptions): Promise<void> => {
        const { pageSize, pageIndex, sortBy, validFilters } = fetchOptions;
        try {
            this.cachedFetchOptions = fetchOptions;
            this.setState({ isLoading: true});
            // cancel in-flight claims before setting group
            this.authContext.cancelGroup('claim-listing');
            this.authContext.setGroup('claim-listing');

            // load claim listing data
            // const dateRangeFilter = this.createDateRangeFilter();
            const sortAndOrder = parseSortBy(sortBy, this.presenter.columns);
            const isInstitutional: boolean = this.claimType === ClaimType.INSTITUTIONAL;
            // While a responsibility from the widget is selected, the table shows the widget's slice:
            // the same create-date window, so its total matches the chip (FE-151). Added to the
            // request only — the table's filter model has no way to hold a date range.
            const scope = createdScope(validFilters, this.props.rootStore.claimResponsibilityStore.range);
            const claimListing = await this.claimStore.loadClaimListing({
                ...sortAndOrder,
                page: pageIndex || 0,
                pageSize: pageSize as number,
                filters: scope.kind === 'applied' ? [...validFilters, scope.filter] : [...validFilters],
                institutional: isInstitutional
            });
            if (claimListing) {
                this.presenter.onDataUpdate(claimListing.items);
                this.presenter.onDataTotalChange(claimListing.total);
            }
            this.setState({ fetchOptions, isLoading: false });
        } catch (err) {
            this.dialogContext.showError(err);
        }
    };

    private getRowHref = (row: Row<ProviderClaim>): string => {
        const accountId = this.getAccountId();
        return accountId ? `/${accountId}/claims/${row.original.id}` : `/claims/${row.original.id}`;
    };
    private getAccountId = (): string | undefined => {
        const pathname = window.location.pathname;
        const parts = pathname.split('/').filter(Boolean);
        // Check if first part is a UUID (accountId)
        if (parts.length > 0 && parts[0].length === 36) {
            return parts[0];
        }
        return undefined;
    };

    // private createDateRangeFilter = (): ICustomFilter => {
    //     return {
    //         filterType: 'String',
    //         filterId: 'filterCreated',
    //         filterMethod: FilterMethod.BT,
    //         value: `${isoDateTimeFormat(this.startDate)}:${isoDateTimeFormat(this.endDate)}`,
    //     };
    // };

    // private updateColumnsForDateRange = (columns: ITableColumn[]): ITableColumn[] => {
    //     const updateShow = (col: ITableColumn) => {
    //         if (col.id === 'Created') {
    //             return this.dateRangeType === 'billing';
    //         } else if (col.id === 'ServiceDates') {
    //             return this.dateRangeType === 'service';
    //         } else {
    //             return col.show;
    //         }
    //     };
    //     return _.map(columns, col => ({
    //         ...col,
    //         show: updateShow(col),
    //     }));
    // };
}

const CreateClaimButton: React.FC = () => {
    const params = useParams();
    const accountId = params.accountId;
    return (
        <BaseNavLink to={accountId ? `/${accountId}/claims/create` : 'claims/create'} target="_blank">
            {(): React.ReactElement => (
                <BaseButton variant="primary" size="medium">
                    Create Claim
                </BaseButton>
            )}
        </BaseNavLink>
    );
};

export const ClaimListing: React.FC<IObservedRootStore> = (props: IObservedRootStore): React.ReactElement => {
    const authContext = useContext(AuthContext);
    const dialogContext = useContext(DialogContext);
    return <ClaimListPage authContext={authContext} dialogContext={dialogContext} />;
};
